<template>
  <RfBaseModal
    ref="modal"
    title="Edit Engagement Category"
    width="600"
    :disabledPrimary="isSaveDisabled"
    @primary="handleSave"
    @show="syncFromItem"
    @close="handleClose"
  >
    <template #body>
      <div class="flex flex-col gap-3">
        <div class="text-sm text-gray-600">{{ description }}</div>
        <RfSelect
          v-model="selectedEngagementCategory"
          placeholder="Select an engagement category"
          preset="modalMenu"
          :items="engagementCategories"
          variant="outlined"
          :itemDisabled="item => item.disabled"
          :itemDisabledMessage="item => item.disabledMessage"
          sameWidth
          autoWidth
          itemValue="label"
        >
          <template #selected="{ props: tag }">
            <RfColorTag
              :title="tag.label"
              pill
              disabled
              :backgroundColor="getCategoryColor(tag.value)"
            />
          </template>
          <template #item="{ props: tag }">
            <div class="flex flex-col items-start">
              <RfColorTag
                :title="tag.label"
                pill
                disabled
                :backgroundColor="getCategoryColor(tag.value)"
              />
              <span class="text-grey-2 ml-2 text-sm">{{ tag.description }}</span>
            </div>
          </template>
        </RfSelect>
        <RfRadioGroup
          v-if="showRetentionTypeRadios"
          v-model="retentionTypeRadio"
          legend="Retention type"
          requiredTitle
          vertical
          :options="retentionTypeOptions"
          class="[&_.rf-radio-group--wrapper]:gap-y-4"
        />
      </div>
    </template>
  </RfBaseModal>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { useEngagementSubcategories } from "@/utils/composables/useEngagementSubcategories";
import { markNudgeDismissed } from "@/utils/engagementCategoryNudge";
import {
  ENGAGEMENT_CATEGORY_DESCRIPTION,
  getCategoryColor,
} from "@/utils/constants/EngagementCategoryConstants";

const props = defineProps({
  item: { type: Object, default: () => ({}) },
  kind: {
    type: String,
    default: "prompt",
    validator: value => ["prompt", "guide"].includes(value),
  },
});

const emit = defineEmits(["save"]);

const { engagementCategoriesWithRestrictions: engagementCategories, retentionSubcategories } =
  useEngagementCategories();
const { radioOptionsForCategory } = useEngagementSubcategories();

const modal = ref();
const selectedEngagementCategory = ref(null);
const selectedEngagementSubcategory = ref(undefined);

const description = computed(
  () => ENGAGEMENT_CATEGORY_DESCRIPTION[props.kind] ?? ENGAGEMENT_CATEGORY_DESCRIPTION.prompt,
);

// Subcategory is owned by the sequence for in-sequence prompts — never selectable there
const isInSequence = computed(() => Boolean(props.item?.sequence_id));

const showRetentionTypeRadios = computed(
  () => selectedEngagementCategory.value === "retention" && !isInSequence.value,
);

// "none" is the radio sentinel (mirrors RfBulkEditModal); null is the emitted
// no-subcategory value. undefined means no pick yet, which the Save gate requires.
// The None option carries its own description instead of a separate hint block.
const retentionTypeOptions = computed(() =>
  radioOptionsForCategory("retention").map(option =>
    option.value === "none"
      ? { ...option, description: `Select this if the ${props.kind} has no retention type.` }
      : option,
  ),
);

const retentionTypeRadio = computed({
  get: () =>
    selectedEngagementSubcategory.value === undefined
      ? undefined
      : (selectedEngagementSubcategory.value ?? "none"),
  set: value => {
    selectedEngagementSubcategory.value = value === "none" ? null : value;
  },
});

const isSaveDisabled = computed(
  () =>
    !selectedEngagementCategory.value ||
    (showRetentionTypeRadios.value && selectedEngagementSubcategory.value === undefined),
);

const syncFromItem = () => {
  selectedEngagementCategory.value = props.item?.engagement_category || null;
  // Stale server values (subcategories the payload no longer offers) must not stay
  // preselected; a stored null retention subcategory preselects "None"
  selectedEngagementSubcategory.value = showRetentionTypeRadios.value
    ? retentionSubcategories.value.some(s => s.value === props.item?.engagement_subcategory)
      ? props.item?.engagement_subcategory
      : null
    : undefined;
};

watch(selectedEngagementCategory, category => {
  if (category !== "retention") selectedEngagementSubcategory.value = undefined;
});

const show = () => modal.value?.show();
const close = () => modal.value?.close();

const handleSave = () => {
  emit(
    "save",
    selectedEngagementCategory.value,
    showRetentionTypeRadios.value ? selectedEngagementSubcategory.value : null,
  );
  close();
};

const handleClose = () => {
  if (!selectedEngagementCategory.value && !props.item?.engagement_category && props.item?.id) {
    markNudgeDismissed(props.kind, props.item.id);
  }
};

defineExpose({ show, close });
</script>
